// Constants
export const MULTISELECT_OPERATORS = ["IN", "NOT IN"];
export const COMPARISON_OPERATORS = ["=", "!=", "<", "<=", ">", ">="];
export const TEXT_OPERATORS = ["LIKE", "NOT LIKE", "ILIKE", "NOT ILIKE"];
export const BETWEEN_OPERATORS = ["BETWEEN", "NOT BETWEEN"];
export const NULL_OPERATORS = ["IS NULL", "IS NOT NULL"];
export const EXTENDED_MULTISELECT_OPERATORS = [...MULTISELECT_OPERATORS, ...BETWEEN_OPERATORS];

export const INITIAL_CONDITION_GROUP = {
  id: 1,
  groupOperator: null,
  conditions: [
    { field: "", operator: "=", value: "" }
  ]
};

// Utility helpers
export const stripHtmlTags = (str) => {
  if (!str) return str;
  return str.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

// Field configuration helpers
export const getFieldConfig = (fieldName, columnListValues) => {
  if (!columnListValues || columnListValues.length === 0) return null;
  return columnListValues.find(item => item.Field === fieldName);
};

export const isSelectField = (fieldName, columnListValues) => {
  const config = getFieldConfig(fieldName, columnListValues);
  return config && config.Flag === "Y";
};

export const shouldUseInputField = (operator) => {
  return COMPARISON_OPERATORS.includes(operator);
};

export const getAvailableFields = (columnListValues) => {
  if (!columnListValues || columnListValues.length === 0) return [];
  
  const uniqueFields = [...new Set(columnListValues.map(item => item.Field))];
  return uniqueFields.sort();
};

// Options are now handled directly in QueryBuilder component using fieldValueOptionsMap

// Condition management helpers
export const addConditionToGroup = (groups, groupId) => {
  return groups.map(group => 
    group.id === groupId 
      ? {
          ...group,
          conditions: [
            ...group.conditions,
            { andOr: "AND", field: "", operator: "=", value: "" }
          ]
        }
      : group
  );
};

export const addNewGroup = (conditionGroups) => {
  const newId = Math.max(...conditionGroups.map(g => g.id)) + 1;
  return [
    ...conditionGroups,
    {
      id: newId,
      groupOperator: "OR",
      conditions: [{ field: "", operator: "=", value: "" }]
    }
  ];
};

export const removeConditionFromGroup = (groups, groupId, conditionIndex) => {
  return groups
    .map(group =>
      group.id === groupId
        ? {
            ...group,
            conditions: group.conditions.filter((_, idx) => idx !== conditionIndex)
          }
        : group
    )
    .filter(group => group.conditions.length > 0);
};

export const removeGroup = (groups, groupId) => {
  if (groups.length > 1) {
    return groups.filter(g => g.id !== groupId);
  }
  return groups;
};

export const updateCondition = (groups, groupId, conditionIndex, field, value, columnListValues) => {
  return groups.map(group => {
    if (group.id !== groupId) return group;
    
    const newConditions = group.conditions.map((condition, idx) => {
      if (idx !== conditionIndex) return condition;
      
      const updatedCondition = { ...condition, [field]: value };
      
      // Reset value when operator changes to prevent type mismatches
      if (field === 'operator') {
        const isNewMultiSelect = MULTISELECT_OPERATORS.includes(value);
        const isNewBetween = BETWEEN_OPERATORS.includes(value);
        const isNewLike = TEXT_OPERATORS.includes(value);
        const isNewNull = NULL_OPERATORS.includes(value);
        const wasMultiSelect = MULTISELECT_OPERATORS.includes(condition.operator);
        const wasBetween = BETWEEN_OPERATORS.includes(condition.operator);
        const wasLike = TEXT_OPERATORS.includes(condition.operator);
        const wasNull = NULL_OPERATORS.includes(condition.operator);
        
        // If switching between different input types, reset the value
        if (isNewMultiSelect !== wasMultiSelect || 
            isNewBetween !== wasBetween || 
            isNewLike !== wasLike ||
            isNewNull !== wasNull) {
          if (isNewNull) {
            updatedCondition.value = ""; // NULL operators don't need a value
          } else if (isNewMultiSelect || isNewBetween || isNewLike) {
            updatedCondition.value = [];
          } else {
            updatedCondition.value = "";
          }
        }
      }
      
      // Reset value when field changes
      if (field === 'field') {
        const isMultiSelectOp = MULTISELECT_OPERATORS.includes(condition.operator);
        const isBetweenOp = BETWEEN_OPERATORS.includes(condition.operator);
        const isLikeOp = TEXT_OPERATORS.includes(condition.operator);
        const isNullOp = NULL_OPERATORS.includes(condition.operator);
        
        if (isNullOp) {
          updatedCondition.value = "";
        } else if (isMultiSelectOp || isBetweenOp || isLikeOp) {
          updatedCondition.value = [];
        } else {
          updatedCondition.value = "";
        }
      }
      
      return updatedCondition;
    });
    
    return { ...group, conditions: newConditions };
  });
};

export const updateGroupOperator = (groups, groupId, operator) => {
  return groups.map(group =>
    group.id === groupId ? { ...group, groupOperator: operator } : group
  );
};

// Validation helpers
export const validateQueryBuilder = (queryName, conditionGroups) => {
  const errors = {
    queryName: "",
    groups: {}
  };

  // Validate query name
  if (!queryName.trim()) {
    errors.queryName = "Group name is required";
  }

  // Validate each group and condition
  conditionGroups.forEach((group) => {
    errors.groups[group.id] = {
      conditions: {}
    };

    group.conditions.forEach((condition, condIdx) => {
      const conditionErrors = {
        field: "",
        operator: "",
        value: ""
      };

      // Validate field
      if (!condition.field) {
        conditionErrors.field = "Field is required";
      }

      // Validate operator
      if (!condition.operator) {
        conditionErrors.operator = "Operator is required";
      }

      // Validate value based on operator type
      if (condition.field && condition.operator) {
        const isBetweenOp = BETWEEN_OPERATORS.includes(condition.operator);
        const isMultiSelectOp = MULTISELECT_OPERATORS.includes(condition.operator);
        const isLikeOp = TEXT_OPERATORS.includes(condition.operator);
        const isNullOp = NULL_OPERATORS.includes(condition.operator);

        if (isNullOp) {
          // NULL operators don't require a value
          // No validation needed
        } else if (isBetweenOp) {
          // BETWEEN requires exactly 2 values
          if (!Array.isArray(condition.value) || condition.value.length === 0) {
            conditionErrors.value = "Please select exactly 2 values for BETWEEN operator";
          } else if (condition.value.length === 1) {
            conditionErrors.value = "Please select one more value (2 values required)";
          } else if (condition.value.length > 2) {
            conditionErrors.value = "Please select only 2 values for BETWEEN operator";
          }
        } else if (isMultiSelectOp || isLikeOp) {
          // IN, NOT IN, LIKE, NOT LIKE, ILIKE, NOT ILIKE require at least one value
          if (!Array.isArray(condition.value) || condition.value.length === 0) {
            conditionErrors.value = "Please select at least one value";
          }
        } else {
          // Regular operators require a non-empty value
          if (!condition.value || (typeof condition.value === 'string' && !condition.value.trim())) {
            conditionErrors.value = "Value is required";
          }
        }
      }

      errors.groups[group.id].conditions[condIdx] = conditionErrors;
    });
  });

  return errors;
};

export const hasValidationErrors = (errors) => {
  if (!errors || typeof errors !== 'object') {
    return false;
  }

  // Check query name error
  if (errors.queryName) {
    return true;
  }

  // Check query error
  if (errors.queryError) {
    return true;
  }

  // Check group errors — inspect error *messages* (strings), not condition field values
  if (errors.groups && typeof errors.groups === 'object') {
    return Object.values(errors.groups).some(group => {
      if (!group || typeof group !== 'object' || !group.conditions) {
        return false;
      }
      return Object.values(group.conditions).some(condition => {
        if (!condition || typeof condition !== 'object') {
          return false;
        }
        // A non-empty string means there is an actual error message
        return (
          (typeof condition.field === 'string' && condition.field !== '') ||
          (typeof condition.operator === 'string' && condition.operator !== '') ||
          (typeof condition.value === 'string' && condition.value !== '')
        );
      });
    });
  }

  return false;
};

export const getErrorFromPath = (errors, path) => {
  if (!errors || typeof errors !== 'object') {
    return "";
  }

  const pathParts = path.split('.');
  let error = errors;

  for (const part of pathParts) {
    if (error && typeof error === 'object' && part in error) {
      error = error[part];
    } else {
      return "";
    }
  }
  
  return typeof error === 'string' ? error : "";
};

// Input type detection helpers
export const isBetweenOperator = (operator) => {
  return BETWEEN_OPERATORS.includes(operator);
};

export const isMultiSelectOperator = (operator) => {
  return MULTISELECT_OPERATORS.includes(operator);
};

export const isLikeOperator = (operator) => {
  return TEXT_OPERATORS.includes(operator);
};

export const isNullOperator = (operator) => {
  return NULL_OPERATORS.includes(operator);
};

export const getInputType = (condition, columnListValues) => {
  const isBetweenOp = isBetweenOperator(condition.operator);
  const isMultiSelectOp = isMultiSelectOperator(condition.operator);
  const isLikeOp = isLikeOperator(condition.operator);
  const isNullOp = isNullOperator(condition.operator);
  const hasSelectOptions = isSelectField(condition.field, columnListValues);
  const useInputField = shouldUseInputField(condition.operator);

  if (isNullOp) return 'null';
  if (isBetweenOp) return 'between';
  if (isLikeOp) return 'multientry';
  if (isMultiSelectOp) return 'multiselect';
  if (useInputField) return 'input';
  if (hasSelectOptions) return 'select';
  return 'input';
};

// Query building helper
export const buildWhereClause = (conditionGroups) => {
  if (conditionGroups.length === 0) return "WHERE";
  
  let clause = "WHERE ";
  
  conditionGroups.forEach((group, groupIdx) => {
    // Add group operator before subsequent groups
    if (groupIdx > 0 && group.groupOperator) {
      clause += ` ${group.groupOperator} `;
    }
    
    // Filter out conditions without a field
    const validConditions = group.conditions.filter(c => c.field);
    
    if (validConditions.length === 0) return; // Skip empty groups
    
    // Build conditions string
    const groupConditions = validConditions
      .map((c, idx) => {
        let conditionStr = "";
        const prefix = idx > 0 ? c.andOr + " " : "";
        
        // Handle different operators
        if (isNullOperator(c.operator)) {
          // IS NULL / IS NOT NULL operators
          conditionStr = `${prefix}${c.field} ${c.operator}`;
        } else if (isLikeOperator(c.operator)) {
          // LIKE/NOT LIKE/ILIKE/NOT ILIKE with multiple values: convert to OR conditions
          const valueArray = Array.isArray(c.value) ? c.value : (c.value ? [c.value] : []);
          if (valueArray.length > 0) {
            const likeConditions = valueArray.map(v => 
              `${c.field} ${c.operator} '%${v}%'`
            ).join(' OR ');
            conditionStr = `${prefix}(${likeConditions})`;
          } else {
            conditionStr = `${prefix}${c.field} ${c.operator} '%%'`;
          }
        } else if (isMultiSelectOperator(c.operator)) {
          // IN/NOT IN operators
          const valueArray = Array.isArray(c.value) ? c.value : (c.value ? [c.value] : []);
          const valueStr = `(${valueArray.map(v => `'${v}'`).join(', ')})`;
          conditionStr = `${prefix}${c.field} ${c.operator} ${valueStr}`;
        } else if (isBetweenOperator(c.operator)) {
          // BETWEEN operator
          const valueArray = Array.isArray(c.value) ? c.value : (c.value ? [c.value] : []);
          if (valueArray.length === 2) {
            conditionStr = `${prefix}${c.field} ${c.operator} '${valueArray[0]}' AND '${valueArray[1]}'`;
          } else if (valueArray.length === 1) {
            conditionStr = `${prefix}${c.field} ${c.operator} '${valueArray[0]}' AND ''`;
          } else {
            conditionStr = `${prefix}${c.field} ${c.operator} '' AND ''`;
          }
        } else {
          // Regular comparison operators (=, !=, <, >, etc)
          const value = Array.isArray(c.value) ? (c.value[0] || '') : (c.value || '');
          conditionStr = `${prefix}${c.field} ${c.operator} '${value}'`;
        }
        
        return conditionStr;
      })
      .join(" ");
    
    // Always wrap in brackets if there are multiple groups
    if (conditionGroups.length > 1) {
      clause += `( ${groupConditions} )`;
    } else {
      clause += groupConditions;
    }
  }); 
  
  return clause + ";";
};

// State management helpers
export const getInitialState = () => ({
  queryName: "",
  conditionGroups: [INITIAL_CONDITION_GROUP],
  fieldLoadingStates: {},
  fieldOptionsCache: {}
});

export const resetState = () => getInitialState();

// Transform query builder data to data group structure for API/Redux
export const getOutputStructure = (conditionGroups, columnListValues) => {
  return conditionGroups
    .filter(group => group.conditions.some(c => c.field)) // Only include groups with at least one valid condition
    .map((group, groupIdx) => {
      // Get valid conditions (must have a field)
      const validConditions = group.conditions.filter(c => c.field);
      
      return {
        connector: groupIdx === 0 ? "" : (group.groupOperator || "OR"),
        data: validConditions.map((condition, condIdx) => {
          // Get field configuration to determine flag
          const fieldConfig = getFieldConfig(condition.field, columnListValues);
          const flag = fieldConfig?.Flag || "N";
          
          // Normalize value to always be an array
          let values = [];
          if (isNullOperator(condition.operator)) {
            // NULL operators don't have values
            values = [];
          } else if (Array.isArray(condition.value)) {
            values = condition.value;
          } else if (condition.value !== "" && condition.value !== null && condition.value !== undefined) {
            // Always convert to array, even for input field values
            values = [String(condition.value)];
          }
          
          return {
            connector: condIdx === 0 ? "" : (condition.andOr || "AND"),
            name: condition.field,
            operator: condition.operator,
            flag: flag,
            values: values
          };
        })
      };
    });
};

// Prepare data for backend API - keep the grouped structure
export const prepareOutputPayload = (queryName, conditionGroups, whereClause, columnListValues) => {
  const dataGroupStructure = getOutputStructure(conditionGroups, columnListValues);
  
  // Add type field to each condition in the grouped structure
  const fields = dataGroupStructure.map(group => ({
    connector: group.connector,
    data: group.data.map(condition => ({
      connector: condition.connector,
      field: condition.name,
      operator: condition.operator,
      flag: condition.flag,
      type: condition.flag === "Y" ? "dropdown" : "input",
      values: condition.values
    }))
  }));
  
  return {
    title: queryName,
    query: whereClause,
    fields: fields
  };
};

export function parseBackendErrors(backendErrors) {
    if (!backendErrors || typeof backendErrors !== 'object') {
        return {};
    }

    const qbErrors = {
        queryName: "",
        groups: {}
    };

    Object.keys(backendErrors).forEach(errorPath => {
        const errorObj = backendErrors[errorPath];
        const errorMessage = (typeof errorObj === "string") ? errorObj : errorObj?.message || errorObj?.properties?.message || "Invalid value";

        // Parse paths like "fields.0.data.0.operator" or "title"
        if (errorPath === 'title') {
            qbErrors.queryName = errorMessage;
        } else if (errorPath === 'query') {
            qbErrors.queryError = errorMessage;
        } else if (errorPath.startsWith('fields.')) {
            // Parse: fields.0.data.0.operator
            const pathParts = errorPath.split('.');
            
            if (pathParts.length >= 4) {
                const groupIndex = parseInt(pathParts[1]); // 0
                const fieldIndex = parseInt(pathParts[3]); // 0
                const fieldName = pathParts[4]; // operator, field, value
                
                // Initialize group if doesn't exist
                const groupId = groupIndex + 1; // Convert to 1-based ID
                if (!qbErrors.groups[groupId]) {
                    qbErrors.groups[groupId] = { conditions: {} };
                }
                
                // Initialize condition if doesn't exist
                if (!qbErrors.groups[groupId].conditions[fieldIndex]) {
                    qbErrors.groups[groupId].conditions[fieldIndex] = {
                        field: "",
                        operator: "",
                        value: ""
                    };
                }
                
                // Set the error message
                qbErrors.groups[groupId].conditions[fieldIndex][fieldName] = errorMessage;
            }
        }
    });

    return qbErrors;
};
